import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useLazyQuery } from '@apollo/client';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Spin } from 'antd';

import { orderReceivableStatus } from '~constants/order';
import { QUERY_LEAD_GENERATION_DETAIL_BY_ID } from '../queries';
import { tabList } from './constants';

import Header from './components/header';
import ProductInfo from './components/product-info';
import BookingInfo from './components/booking-info';
import CommissionInfo from './components/commission-info';
import StudentInfo from './components/student-info';
import ReconciliationHistory from './components/reconciliation-history';
import UpdateStatusModal from '../components/modal';
import EditCreditNoteModal from '../components/modal/edit-credit-note';
import EditBookingInformationModal from '../components/modal/edit-booking-information';
import EditProductInformationModal from '../components/modal/edit-product-information';

export default function OrderDetail({ match }) {
  const { t } = useTranslation();
  const history = useHistory();
  const updateStatusModalRef = useRef();
  const [isFixed, setIsFixed] = useState(false);
  const [visibleProductModal, setVisibleProductModal] = useState(false);
  const [visibleBookingModal, setVisibleBookingModal] = useState(false);
  const [visibleCommissionModal, setVisibleCommissionModal] = useState(false);
  const [orderInfo, setOrderInfo] = useState({});
  const [activeTab, setActiveTab] = useState(tabList[0]);

  const showExtra = useMemo(
    () =>
      [
        orderReceivableStatus.NEW,
        orderReceivableStatus.PENDING_APPROVAL,
        orderReceivableStatus.READY_FOR_APPROVAL,
      ].includes(orderInfo?.status),
    [orderInfo?.status],
  );

  const showEdit = useMemo(
    () =>
      [
        orderReceivableStatus.INVOICED,
        orderReceivableStatus.PAID,
        orderReceivableStatus.PARTIALLY_PAID,
      ].includes(orderInfo?.status),
    [orderInfo?.status],
  );

  const handleSelectTab = tab => {
    setActiveTab(tab);
  };

  const handleScrollHref = () => {
    const scrollTop = document.documentElement.scrollTop;
    setIsFixed(scrollTop > 250);
  };

  const handleOpenUpdateModal = row => {
    updateStatusModalRef.current?.handleOpenModal(row);
  };

  const queryDetailData = () => {
    const receivableId = match?.params?.id;
    if (receivableId) {
      queryDetail({ variables: { id: decodeURIComponent(receivableId) } });
    }
  };

  const [queryDetail, { loading }] = useLazyQuery(QUERY_LEAD_GENERATION_DETAIL_BY_ID, {
    fetchPolicy: 'no-cache',
    onCompleted: res => {
      setOrderInfo(res.getOrderReceivable);
    },
  });

  useEffect(() => {
    queryDetailData();
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScrollHref);
    return () => {
      window.removeEventListener('scroll', handleScrollHref);
    };
  }, []);

  return (
    <div className="reconciliation__detail">
      <Choose>
        <When condition={loading}>
          <Spin className="loading-spin" />
        </When>
        <Otherwise>
          <Header
            orderInfo={orderInfo}
            t={t}
            history={history}
            handleOpenUpdateModal={handleOpenUpdateModal}
          />
          <div className={classnames('tab-wrapper', isFixed && 'fixed-container')}>
            <For of={tabList} each="item" index="index">
              <a
                onClick={() => handleSelectTab(item)}
                href={`#${item?.toLowerCase()}`}
                className={classnames('tab-link', { 'active-link': activeTab === item })}
              >
                {t(`cms.reconciliation.lead_generation.detail.${item?.toLowerCase()}.tab`)}
              </a>
            </For>
          </div>
          <div className="info-wrapper">
            <div id="product_info">
              <ProductInfo
                t={t}
                productInfo={orderInfo?.order}
                showExtra={showExtra}
                setVisibleModal={setVisibleProductModal}
                id="product_info"
              />
            </div>
            <div id="booking_info">
              <BookingInfo
                t={t}
                bookingInfo={orderInfo?.order}
                showExtra={showExtra}
                setVisibleModal={setVisibleBookingModal}
              />
            </div>
            <div id="commission_info">
              <CommissionInfo
                id="commission_info"
                t={t}
                commissionInfo={{
                  invoiceNumber: orderInfo?.invoiceNumber,
                  commissionTriggerPoint: orderInfo?.order?.commissionTriggerPoint,
                  ...orderInfo?.commission,
                }}
                showEdit={showEdit}
                setVisibleModal={setVisibleCommissionModal}
              />
            </div>
            <div id="student_info">
              <StudentInfo t={t} studentInfo={orderInfo?.order?.student} id="student_info" />
            </div>
            <div id="reconciliation_history">
              <ReconciliationHistory
                id="reconciliation_history"
                t={t}
                reconciliationHistoryInfo={
                  orderInfo?.order?.receivables?.[0]?.orderReceivableProcesses
                }
              />
            </div>
          </div>
        </Otherwise>
      </Choose>
      <UpdateStatusModal
        refetchData={() => queryDetailData()}
        wrappedComponentRef={form => (updateStatusModalRef.current = form)}
      />
      <If condition={visibleProductModal}>
        <EditProductInformationModal
          refetchData={() => queryDetailData()}
          orderInfo={orderInfo}
          visible={visibleProductModal}
          setVisible={setVisibleProductModal}
        />
      </If>
      <If condition={visibleBookingModal}>
        <EditBookingInformationModal
          refetchData={() => queryDetailData()}
          orderInfo={orderInfo}
          visible={visibleBookingModal}
          setVisible={setVisibleBookingModal}
        />
      </If>
      <If condition={visibleCommissionModal}>
        <EditCreditNoteModal
          refetchData={() => queryDetailData()}
          orderInfo={orderInfo}
          visible={visibleCommissionModal}
          setVisible={setVisibleCommissionModal}
        />
      </If>
    </div>
  );
}
